import React, { useState, useMemo, useEffect, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, X, ChevronDown, Check, Users, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Settings2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { useCandidates } from '../../hooks/useCandidates';
import { supabase } from '../../lib/supabaseClient';
import AddCandidateModal from './AddCandidateModal';
import DeleteCandidateDialog from './DeleteCandidateDialog';
import MobileTableCard from '../ui/mobile-table-card';
import ListContainer from '../ui/list-container';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Generate a 4-character hash from UUID for candidate display ID
const generateCandidateDisplayId = (uuid) => {
  if (!uuid) return 'XXXX';
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const displayId = Math.abs(hash).toString(36).toUpperCase().substring(0, 4).padStart(4, '0');
  return displayId;
};

// Filter options
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Blacklisted', label: 'Blacklisted' },
  { value: 'Inactive', label: 'Inactive' },
];

const CLEARANCE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'Baseline', label: 'Baseline' },
  { value: 'NV1', label: 'NV1' },
  { value: 'NV2', label: 'NV2' },
  { value: 'TSPV', label: 'TSPV' },
];

const STATE_OPTIONS = [
  { value: 'ACT', label: 'ACT' },
  { value: 'NSW', label: 'NSW' },
  { value: 'VIC', label: 'VIC' },
  { value: 'QLD', label: 'QLD' },
  { value: 'SA', label: 'SA' },
  { value: 'WA', label: 'WA' },
  { value: 'TAS', label: 'TAS' },
  { value: 'NT', label: 'NT' },
];

const PAYROLL_OPTIONS = [
  { value: 'PAYG', label: 'PAYG' },
  { value: 'ABN', label: 'ABN (Contractor)' },
];

const CandidatesList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Delete candidate state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Multi-select filters
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedClearances, setSelectedClearances] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedPayrollTypes, setSelectedPayrollTypes] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);

  // Skills master list and search
  const [allSkills, setAllSkills] = useState([]);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
  const [debouncedSkillsSearchTerm, setDebouncedSkillsSearchTerm] = useState('');

  // Positions master list and search
  const [allPositions, setAllPositions] = useState([]);
  const [positionsSearchTerm, setPositionsSearchTerm] = useState('');
  const [debouncedPositionsSearchTerm, setDebouncedPositionsSearchTerm] = useState('');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    alias: true,
    position: true,
    email: true,
    phone: true,
    location: true,
    clearance: true,
    skills: true,
    status: true
  });

  // Sorting
  const [sortField, setSortField] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const { candidates: allCandidates, loading, error, refetch } = useCandidates({
    search: debouncedSearchTerm,
  });

  // Debounce search terms (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSkillsSearchTerm(skillsSearchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [skillsSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedPositionsSearchTerm(positionsSearchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [positionsSearchTerm]);

  // Fetch skills from skills_master table
  useEffect(() => {
    const fetchSkills = async () => {
      const { data, error } = await supabase
        .from('skills_master')
        .select('id, name, category')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching skills:', error);
      } else {
        setAllSkills(data || []);
      }
    };

    fetchSkills();
  }, []);

  // Fetch positions from positions_master table
  useEffect(() => {
    const fetchPositions = async () => {
      const { data, error } = await supabase
        .from('positions_master')
        .select('id, name, category')
        .eq('status', 'active')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching positions:', error);
      } else {
        setAllPositions(data || []);
      }
    };

    fetchPositions();
  }, []);

  const getClearanceBadgeColor = (level) => {
    const colors = {
      'TSPV': 'bg-purple-100 text-purple-800',
      'NV2': 'bg-red-100 text-red-800',
      'NV1': 'bg-orange-100 text-orange-800',
      'Baseline': 'bg-blue-100 text-blue-800',
      'None': 'bg-secondary text-secondary-foreground',
    };
    return colors[level] || 'bg-secondary text-secondary-foreground';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'On Hold': 'bg-yellow-100 text-yellow-800',
      'Blacklisted': 'bg-red-100 text-red-800',
      'Inactive': 'bg-secondary text-secondary-foreground',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };

  // Filter and sort candidates
  const filteredAndSortedCandidates = useMemo(() => {
    let filtered = allCandidates.filter((candidate) => {
      // Status filter
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(candidate.status)) {
        return false;
      }

      // Clearance filter
      if (selectedClearances.length > 0) {
        const clearance = candidate.clearance_level || 'None';
        if (!selectedClearances.includes(clearance)) {
          return false;
        }
      }

      // State filter
      if (selectedStates.length > 0 && !selectedStates.includes(candidate.location_state)) {
        return false;
      }

      // Payroll type filter
      if (selectedPayrollTypes.length > 0 && !selectedPayrollTypes.includes(candidate.payroll_type)) {
        return false;
      }

      // Skills filter - candidate must have at least one of the selected skills
      if (selectedSkills.length > 0) {
        const candidateSkillIds = candidate.candidate_skills?.map(cs => cs.skill?.id) || [];
        const hasMatchingSkill = selectedSkills.some(skillId => candidateSkillIds.includes(skillId));
        if (!hasMatchingSkill) {
          return false;
        }
      }

      // Position filter
      if (selectedPositions.length > 0 && !selectedPositions.includes(candidate.position_id)) {
        return false;
      }

      return true;
    });

    // Sort candidates
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'phone':
          aValue = a.phone || '';
          bValue = b.phone || '';
          break;
        case 'location':
          aValue = a.location_state || '';
          bValue = b.location_state || '';
          break;
        case 'clearance':
          aValue = a.clearance_level || '';
          bValue = b.clearance_level || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'updated_at':
        default:
          aValue = new Date(a.updated_at || a.created_at);
          bValue = new Date(b.updated_at || b.created_at);
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allCandidates, selectedStatuses, selectedClearances, selectedStates, selectedPayrollTypes, selectedSkills, selectedPositions, sortField, sortDirection]);

  // Calculate pagination
  const totalItems = filteredAndSortedCandidates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedStatuses, selectedClearances, selectedStates, selectedPayrollTypes, selectedSkills, selectedPositions, itemsPerPage]);

  // Get current page items
  const candidates = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedCandidates.slice(startIndex, endIndex);
  }, [filteredAndSortedCandidates, currentPage, itemsPerPage]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  // Toggle filter selection
  const toggleFilter = (filterArray, setFilter, value) => {
    setFilter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedClearances([]);
    setSelectedStates([]);
    setSelectedPayrollTypes([]);
    setSelectedSkills([]);
    setSelectedPositions([]);
    setSearchTerm('');
  };

  // Count active filters
  const activeFiltersCount =
    selectedStatuses.length +
    selectedClearances.length +
    selectedStates.length +
    selectedPayrollTypes.length +
    selectedSkills.length +
    selectedPositions.length +
    (searchTerm ? 1 : 0);

  // Remove individual filter
  const removeFilter = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setSelectedStatuses((prev) => prev.filter((item) => item !== value));
        break;
      case 'clearance':
        setSelectedClearances((prev) => prev.filter((item) => item !== value));
        break;
      case 'state':
        setSelectedStates((prev) => prev.filter((item) => item !== value));
        break;
      case 'payroll':
        setSelectedPayrollTypes((prev) => prev.filter((item) => item !== value));
        break;
      case 'skill':
        setSelectedSkills((prev) => prev.filter((item) => item !== value));
        break;
      case 'position':
        setSelectedPositions((prev) => prev.filter((item) => item !== value));
        break;
      case 'search':
        setSearchTerm('');
        break;
    }
  };

  // Filter skills list based on search term
  const filteredSkills = useMemo(() => {
    if (!debouncedSkillsSearchTerm) return allSkills;
    const searchLower = debouncedSkillsSearchTerm.toLowerCase();
    return allSkills.filter(skill =>
      skill.name.toLowerCase().includes(searchLower)
    );
  }, [allSkills, debouncedSkillsSearchTerm]);

  // Filter positions list based on search term
  const filteredPositions = useMemo(() => {
    if (!debouncedPositionsSearchTerm) return allPositions;
    const searchLower = debouncedPositionsSearchTerm.toLowerCase();
    return allPositions.filter(position =>
      position.name.toLowerCase().includes(searchLower)
    );
  }, [allPositions, debouncedPositionsSearchTerm]);

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle delete candidate
  const handleDeleteClick = (candidate, event) => {
    event.stopPropagation(); // Prevent row click navigation
    setCandidateToDelete(candidate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (candidateId) => {
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_URL}/admin/candidates/${candidateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete candidate');
      }

      // Refetch candidates after successful deletion
      await refetch();
    } catch (err) {
      console.error('Error deleting candidate:', err);
      throw new Error(err.message || 'Failed to delete candidate');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Error loading candidates: {error}</p>
      </div>
    );
  }

  return (
    <ListContainer>
      {/* Header */}
      <ListContainer.Header>
        <ListContainer.Title
          title="Candidates"
          subtitle="Manage your candidate database"
        />
        <ListContainer.Actions>
          <Button onClick={() => setAddModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create
          </Button>
        </ListContainer.Actions>
      </ListContainer.Header>

      {/* Filters and Table Card */}
      <Card>
        <CardHeader className="px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Filter Dropdowns */}
              <div className="flex flex-wrap gap-2">
              {/* Status Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    <Filter className="mr-2 h-4 w-4" />
                    Status
                    {selectedStatuses.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedStatuses.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 space-y-1">
                    {STATUS_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleFilter(selectedStatuses, setSelectedStatuses, option.value)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {selectedStatuses.includes(option.value) && (
                            <Check className="h-3 w-3" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clearance Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    Clearance
                    {selectedClearances.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedClearances.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 space-y-1">
                    {CLEARANCE_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleFilter(selectedClearances, setSelectedClearances, option.value)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {selectedClearances.includes(option.value) && (
                            <Check className="h-3 w-3" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* State Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    Location
                    {selectedStates.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedStates.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 space-y-1">
                    {STATE_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleFilter(selectedStates, setSelectedStates, option.value)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {selectedStates.includes(option.value) && (
                            <Check className="h-3 w-3" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Payroll Type Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    Payroll
                    {selectedPayrollTypes.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedPayrollTypes.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 space-y-1">
                    {PAYROLL_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleFilter(selectedPayrollTypes, setSelectedPayrollTypes, option.value)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {selectedPayrollTypes.includes(option.value) && (
                            <Check className="h-3 w-3" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Skills Filter with Search */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    Skills
                    {selectedSkills.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedSkills.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  {/* Search input */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Search skills..."
                        value={skillsSearchTerm}
                        onChange={(e) => setSkillsSearchTerm(e.target.value)}
                        className="pl-8 h-8"
                      />
                    </div>
                  </div>
                  {/* Skills list with checkboxes */}
                  <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
                    {filteredSkills.length > 0 ? (
                      filteredSkills.map((skill) => (
                        <div
                          key={skill.id}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleFilter(selectedSkills, setSelectedSkills, skill.id)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedSkills.includes(skill.id) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <div className="flex-1 flex items-center justify-between">
                            <span className="text-sm">{skill.name}</span>
                            {skill.category && (
                              <Badge variant="outline" className="text-xs ml-2">
                                {skill.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        No skills found
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Position Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 border-dashed">
                    Position
                    {selectedPositions.length > 0 && (
                      <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
                        {selectedPositions.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  {/* Search input */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Search positions..."
                        value={positionsSearchTerm}
                        onChange={(e) => setPositionsSearchTerm(e.target.value)}
                        className="pl-8 h-8"
                      />
                    </div>
                  </div>
                  {/* Positions list with checkboxes */}
                  <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
                    {filteredPositions.length > 0 ? (
                      filteredPositions.map((position) => (
                        <div
                          key={position.id}
                          className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                          onClick={() => toggleFilter(selectedPositions, setSelectedPositions, position.id)}
                        >
                          <div className="flex items-center justify-center w-4 h-4 border rounded">
                            {selectedPositions.includes(position.id) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <div className="flex-1 flex items-center justify-between">
                            <span className="text-sm">{position.name}</span>
                            {position.category && (
                              <Badge variant="outline" className="text-xs ml-2">
                                {position.category}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                        No positions found
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clear All Filters */}
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  size="sm"
                  className="h-9"
                >
                  Reset
                  <X className="ml-2 h-4 w-4" />
                </Button>
              )}
              </div>

              {/* Search */}
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search candidates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>

              {/* View Columns Button */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="space-y-1">
                    <div className="text-sm font-medium mb-2 px-2">Toggle columns</div>
                    {Object.entries({
                      name: 'Name',
                      alias: 'Alias',
                      position: 'Position',
                      email: 'Email',
                      phone: 'Phone',
                      location: 'Location',
                      clearance: 'Clearance',
                      skills: 'Skills',
                      status: 'Status'
                    }).map(([key, label]) => (
                      <div
                        key={key}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleColumn(key)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {visibleColumns[key] && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">{label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Active Filter Badges */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <Badge variant="secondary" className="pl-2 pr-1">
                  Search: {searchTerm}
                  <X
                    className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                    onClick={() => removeFilter('search')}
                  />
                </Badge>
              )}
              {selectedStatuses.map((status) => (
                <Badge key={status} variant="secondary" className="pl-2 pr-1">
                  Status: {status}
                  <X
                    className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                    onClick={() => removeFilter('status', status)}
                  />
                </Badge>
              ))}
              {selectedClearances.map((clearance) => (
                <Badge key={clearance} variant="secondary" className="pl-2 pr-1">
                  Clearance: {clearance}
                  <X
                    className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                    onClick={() => removeFilter('clearance', clearance)}
                  />
                </Badge>
              ))}
              {selectedStates.map((state) => (
                <Badge key={state} variant="secondary" className="pl-2 pr-1">
                  State: {state}
                  <X
                    className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                    onClick={() => removeFilter('state', state)}
                  />
                </Badge>
              ))}
              {selectedPayrollTypes.map((payroll) => (
                <Badge key={payroll} variant="secondary" className="pl-2 pr-1">
                  Payroll: {payroll}
                  <X
                    className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                    onClick={() => removeFilter('payroll', payroll)}
                  />
                </Badge>
              ))}
              {selectedSkills.map((skillId) => {
                const skill = allSkills.find(s => s.id === skillId);
                return skill ? (
                  <Badge key={skillId} variant="secondary" className="pl-2 pr-1">
                    Skill: {skill.name}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => removeFilter('skill', skillId)}
                    />
                  </Badge>
                ) : null;
              })}
              {selectedPositions.map((positionId) => {
                const position = allPositions.find(p => p.id === positionId);
                return position ? (
                  <Badge key={positionId} variant="secondary" className="pl-2 pr-1">
                    Position: {position.name}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => removeFilter('position', positionId)}
                    />
                  </Badge>
                ) : null;
              })}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop: Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.name && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'name' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.alias && (
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Alias
                    </div>
                  </TableHead>
                )}
                {visibleColumns.position && (
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Current Position
                    </div>
                  </TableHead>
                )}
                {visibleColumns.email && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-1">
                      Email
                      {sortField === 'email' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'email' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.phone && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('phone')}
                  >
                    <div className="flex items-center gap-1">
                      Phone
                      {sortField === 'phone' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'phone' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.location && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center gap-1">
                      Location
                      {sortField === 'location' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'location' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.clearance && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('clearance')}
                  >
                    <div className="flex items-center gap-1">
                      Clearance
                      {sortField === 'clearance' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'clearance' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.skills && (
                  <TableHead>Skills</TableHead>
                )}
                {visibleColumns.status && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortField === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      )}
                      {sortField !== 'status' && <ArrowUpDown className="h-4 w-4 opacity-40" />}
                    </div>
                  </TableHead>
                )}
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map((candidate) => (
                <TableRow
                  key={candidate.id}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/recruiter/candidates/${candidate.id}`)}
                >
                  {visibleColumns.name && (
                    <TableCell className="font-medium">
                      {candidate.first_name} {candidate.last_name}
                    </TableCell>
                  )}
                  {visibleColumns.alias && (
                    <TableCell className="text-muted-foreground">
                      {generateCandidateDisplayId(candidate.id)}
                    </TableCell>
                  )}
                  {visibleColumns.position && (
                    <TableCell>
                      {candidate.position ? candidate.position.name : <span className="text-muted-foreground">N/A</span>}
                    </TableCell>
                  )}
                  {visibleColumns.email && (
                    <TableCell>{candidate.email}</TableCell>
                  )}
                  {visibleColumns.phone && (
                    <TableCell>{candidate.phone || 'N/A'}</TableCell>
                  )}
                  {visibleColumns.location && (
                    <TableCell>
                      {candidate.location_city && candidate.location_state
                        ? `${candidate.location_city}, ${candidate.location_state}`
                        : candidate.location_city || candidate.location_state || 'N/A'}
                    </TableCell>
                  )}
                  {visibleColumns.clearance && (
                    <TableCell>
                      <Badge className={getClearanceBadgeColor(candidate.clearance_level)}>
                        {candidate.clearance_level || 'None'}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.skills && (
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {candidate.candidate_skills && candidate.candidate_skills.length > 0 ? (
                          candidate.candidate_skills.slice(0, 3).map((cs) => (
                            <Badge
                              key={cs.id}
                              variant="outline"
                              className="text-xs"
                            >
                              {cs.skill?.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">No skills</span>
                        )}
                        {candidate.candidate_skills && candidate.candidate_skills.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{candidate.candidate_skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell>
                      <Badge className={getStatusBadgeColor(candidate.status)}>
                        {candidate.status}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => handleDeleteClick(candidate, e)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* Mobile: Card View */}
          <div className="md:hidden space-y-3 p-2 w-full max-w-full overflow-hidden">
            {candidates.map((candidate) => {
              // Add computed fields for mobile card display
              const candidateWithName = {
                ...candidate,
                name: `${candidate.first_name} ${candidate.last_name}`,
                alias: `Candidate-${generateCandidateDisplayId(candidate.id)}`,
              };

              return (
                <MobileTableCard
                  key={candidate.id}
                  item={candidateWithName}
                  primaryField="name"
                  secondaryField="alias"
                  statusField="status"
                  getStatusVariant={getStatusBadgeColor}
                fields={[
                  {
                    label: 'Position',
                    key: 'position',
                    render: (pos) => pos?.name || 'N/A',
                    showOnMobile: true,
                  },
                  {
                    label: 'Location',
                    key: 'location',
                    render: () => {
                      const city = candidate.location_city;
                      const state = candidate.location_state;
                      return city && state ? `${city}, ${state}` : city || state || 'N/A';
                    },
                    showOnMobile: true,
                  },
                  {
                    label: 'Clearance',
                    key: 'clearance_level',
                    render: (level) => (
                      <Badge className={getClearanceBadgeColor(level)} variant="outline">
                        {level || 'None'}
                      </Badge>
                    ),
                    showOnMobile: true,
                  },
                ]}
                badges={
                  candidate.candidate_skills && candidate.candidate_skills.length > 0
                    ? [
                        ...candidate.candidate_skills.slice(0, 3).map((cs) => ({
                          label: cs.skill?.name,
                          variant: 'outline',
                          className: 'text-[10px]',
                        })),
                        ...(candidate.candidate_skills.length > 3
                          ? [{
                              label: `+${candidate.candidate_skills.length - 3}`,
                              variant: 'secondary',
                              className: 'text-[10px]',
                            }]
                          : []),
                      ]
                    : []
                }
                actions={
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 text-xs"
                      onClick={() => navigate(`/recruiter/candidates/${candidate.id}`)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={(e) => handleDeleteClick(candidate, e)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                }
                onClick={() => navigate(`/recruiter/candidates/${candidate.id}`)}
              />
              );
            })}
          </div>

          {candidates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center border-t">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No candidates found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm || activeFiltersCount > 0
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first candidate'}
              </p>
              {(searchTerm || activeFiltersCount > 0) && (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                  className="mt-4"
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-4 px-4 sm:px-0">
              {/* Items per page selector */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border rounded px-2 py-1 bg-background"
                >
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-muted-foreground">per page</span>
              </div>

              {/* Page info */}
              <div className="text-sm text-muted-foreground">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="min-w-[2.5rem]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Candidate Modal */}
      <AddCandidateModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSuccess={() => {
          refetch();
          setAddModalOpen(false);
        }}
      />

      {/* Delete Candidate Dialog */}
      <DeleteCandidateDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        candidate={candidateToDelete}
        onDelete={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </ListContainer>
  );
};

export default CandidatesList;
